// Display formatting only. Amounts arrive as decimal strings and are never used for arithmetic here.

export type Lang = 'en' | 'hr';

const localeTag: Record<Lang, string> = { en: 'en-IE', hr: 'hr-HR' };

export function formatMoney(amount: string | number, currency: string, lang: Lang, opts?: { signed?: boolean }): string {
  const value = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(value)) return '–';
  return new Intl.NumberFormat(localeTag[lang], {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol',
    signDisplay: opts?.signed ? 'exceptZero' : 'auto',
  }).format(value);
}

export function formatDate(iso: string | Date, lang: Lang, style: 'short' | 'long' = 'short'): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat(localeTag[lang], {
    day: 'numeric',
    month: style === 'long' ? 'long' : 'short',
    year: style === 'long' ? 'numeric' : undefined,
  }).format(date);
}

/** Day header such as "Today" / "Yesterday" falls back to a date; the caller translates the words. */
export function dayDiff(iso: string | Date, now: Date = new Date()): number {
  const d = new Date(iso);
  const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((b - a) / 86_400_000);
}

export function formatMonth(iso: string | Date, lang: Lang): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat(localeTag[lang], { month: 'short' }).format(date);
}

/** `12.5` -> "+12.5%". The API sends percentage points, not a ratio. */
export function formatPercent(points: number, lang: Lang): string {
  return new Intl.NumberFormat(localeTag[lang], { style: 'percent', maximumFractionDigits: 1, signDisplay: 'exceptZero' }).format(points / 100);
}

/** Local calendar day as YYYY-MM-DD, the value of a date input. */
export function toLocalDay(value: string | Date): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
